import { useCallback, useRef } from 'react';

import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { FanguardApiContractsUserUserDetailModel } from 'src/api/generated';
import { MobileDialog } from 'src/components/MobileDialog/MobileDialog';
import ScrollGradient from 'src/components/ScrollGradient';
import { useTheme } from 'src/contexts/ThemeContext';
import { useDeleteUser, useResetUserPassword } from 'src/hooks/api/useUsersEndpoint';
import { useHandleLongPress } from 'src/hooks/useLongPress';
import { useAppNavigation } from 'src/routes';

import { ResetPasswordDescription, ResetPasswordWrapper } from '../../UsersTable.styled';
import {
  ChangePasswordButton,
  DeleteMobileButton,
  EditButton,
  MobileEmail,
  MobileRow,
  RowActions,
  RowActionsWrapper,
  Wrapper,
} from './UsersMobileRow.styled';

interface PeopleMobilePersonRowProps {
  users: FanguardApiContractsUserUserDetailModel[];
  user: FanguardApiContractsUserUserDetailModel;
  activeUserEmail: string | null;
  setActiveUserEmail: (email: string | null) => void;
}

const UsersMobileRow = (props: PeopleMobilePersonRowProps) => {
  const { user, activeUserEmail, setActiveUserEmail } = props;
  const rowRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const { navigateTo } = useAppNavigation();
  const { t } = useTranslation();
  const { mutateAsync } = useDeleteUser();
  const { mutateAsync: resetUserPassword } = useResetUserPassword();
  const { combinedTheme } = useTheme();

  const { handlers } = useHandleLongPress({
    handleClickOutside: () => {
      setTimeout(() => setActiveUserEmail(null), 100);
    },
    handleSetLongPress: (context) => {
      if (context) {
        setActiveUserEmail(context);
      }
    },
    handleLongPressStart: () => {
      setActiveUserEmail(null);
    },
    ref: rowRef,
  });

  const handleRowClick = (e: React.MouseEvent<HTMLElement>) => {
    if (activeUserEmail === user.email) {
      e.stopPropagation();
      e.preventDefault();
      if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation();
      }
    }
  };

  const handleDeleteClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }

    return MobileDialog({
      title: `${t('general.delete')} ${t('users.user')}?`,
      description: t('general.cannotBeUndone'),
      confirmText: t('general.ok').toUpperCase(),
      cancelText: t('general.cancel'),
      onConfirm: async () => {
        if (user.username) {
          await mutateAsync(user.username);
          navigateTo.users();
        }
      },
      theme: combinedTheme,
    });
  };

  const handleClickChangePassword = (e: React.MouseEvent<HTMLElement>) => {
    MobileDialog({
      title: `${t('users.table.resetPassword')}?`,
      description: (
        <ResetPasswordWrapper>
          <ResetPasswordDescription $isDesktop={false}>
            {t('users.table.resetPasswordTitle', { username: user.username })}
          </ResetPasswordDescription>
          <ResetPasswordDescription $isDesktop={false}>
            {t('users.table.resetPasswordDescription', { email: user.email })}
          </ResetPasswordDescription>
        </ResetPasswordWrapper>
      ),
      onConfirm: () => handleResetUserPassword(e),
      theme: combinedTheme,
      confirmText: t('general.ok').toUpperCase(),
      cancelText: t('general.cancel'),
    });
  };
  const handleResetUserPassword = async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.nativeEvent) {
      e.nativeEvent.stopImmediatePropagation();
    }

    if (user.username) {
      await resetUserPassword(user.username);
    }
  };

  const handleClickEditButton = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (e.nativeEvent) {
        e.nativeEvent.stopImmediatePropagation();
      }

      if (user.username) {
        navigateTo.usersDetails(user.username);
      }
    },
    [navigateTo.usersDetails, user.username],
  );

  return (
    <ScrollGradient>
      <Wrapper>
        <MobileRow
          {...handlers(user.email)}
          className={activeUserEmail === user.email ? 'active' : ''}
          onClick={handleRowClick}
          data-active={activeUserEmail === user.email ? 'true' : 'false'}
        >
          <Flex ref={rowRef}>
            <Flex vertical>
              <div>
                {user.firstName} {user.lastName}
              </div>
              <MobileEmail>{user.email}</MobileEmail>
            </Flex>
          </Flex>
        </MobileRow>

        {activeUserEmail === user.email && (
          <RowActionsWrapper ref={actionsRef}>
            <RowActions>
              <ChangePasswordButton
                data-ignore-outside
                aria-label="change password"
                onClick={handleClickChangePassword}
              />
              <EditButton data-ignore-outside onClick={handleClickEditButton} />
              <DeleteMobileButton data-ignore-outside onClick={handleDeleteClick} />
            </RowActions>
          </RowActionsWrapper>
        )}
      </Wrapper>
    </ScrollGradient>
  );
};

export default UsersMobileRow;
